import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Amount } from '../../../web/src/components/display/amount.jsx';
import { WealthSection } from '../../../web/src/screens/reports/wealth-section.jsx';
import { useSiteLang } from '../layout/use-site-lang.js';
import { wealthByTypeData, wealthByCurrencyData, getCurrencyInfo } from './report-utils.js';
import { getBaseCurrency } from './mock-data/mock-data.js';

/**
 * Wealth Breakdown report card — net wealth by account type and by currency,
 * computed from the same mock data as the net worth card so totals match.
 * Reuses the app's WealthSection so the donut charts, swatch column, and
 * hover linking behave exactly as they do in the real report.
 * @returns {JSX.Element}
 */
export function WealthCard() {
	const { t } = useTranslation();
	const lang = useSiteLang();
	const base = getBaseCurrency(lang);
	const byType = useMemo(() => wealthByTypeData(lang), [lang]);
	const byCurrency = useMemo(() => wealthByCurrencyData(lang), [lang]);
	const typeValues = Object.fromEntries(byType.map((row) => [row.type, row.net]));
	const currencyValues = Object.fromEntries(byCurrency.map((row) => [row.currency, row.converted]));
	const nativeValues = Object.fromEntries(byCurrency.map((row) => [row.currency, row.native]));
	const total = byType.reduce((sum, row) => sum + row.net, 0);

	const baseInfo = getCurrencyInfo(base, lang);
	/**
	 * @param {number} amount
	 * @returns {import('react').ReactNode}
	 */
	const amt = (amount) => (
		baseInfo ? <Amount value={amount} decimalPlaces={baseInfo.decimalPlaces} symbol={baseInfo.symbol} /> : String(amount)
	);

	/**
	 * @param {string} code
	 * @param {number} amount
	 * @returns {import('react').ReactNode}
	 */
	const amtNative = (code, amount) => {
		const info = getCurrencyInfo(code, lang);
		return info ? <Amount value={amount} decimalPlaces={info.decimalPlaces} symbol={info.symbol} /> : String(amount);
	};

	return (
		<div className="card report-card">
			<div className="card-header"><h2>{t('report.wealthBreakdown')}</h2></div>
			<div className="card-body report-body">
				<WealthSection
					title={t('report.wealthByType')}
					headName={t('report.colType')}
					ariaLabel={t('report.wealthByType')}
					values={typeValues}
					labelOf={(type) => t('option.' + type)}
					amountOf={(_key, value) => amt(value)}
					total={total}
				/>
				<WealthSection
					title={t('report.netWealthByCurrency')}
					headName={t('report.colCurrency')}
					headName2={t('report.colNetValueIn', { symbol: baseInfo ? baseInfo.symbol : base })}
					ariaLabel={t('report.netWealthByCurrency')}
					values={currencyValues}
					labelOf={(code) => code}
					amountOf={(code, value) => (code === null ? amt(value) : amtNative(code, nativeValues[code]))}
					amountOf2={(code, value) => amt(value)}
					subtotalOf={() => '\u2014'}
					total={total}
				/>
			</div>
		</div>
	);
}
